import { useCreateRentAmendment } from '@services/amendments/mutations/useCreateRentAmendment'
import { useRentAmendmentPreview } from '@services/amendments/mutations/useRentAmendmentPdf'
import { useLandlordAmendmentDefault } from '@services/amendments/queries/useRentAmendments'
import {
  PRICE_FIELDS,
  type PriceField,
  type Prices,
  type RentAmendmentBody,
  type RentAmendmentKind,
  type RentAmendmentSide
} from '@services/amendments/types'
import { addMonths, format, parseISO, startOfMonth } from 'date-fns'
import {
  AlertTriangle,
  Eye,
  FileSignature,
  Send,
  TrendingUp
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

import { money, round2 } from '../utils'

const sum = (prices: Prices) =>
  round2(PRICE_FIELDS.reduce((acc, field) => acc + (prices[field] || 0), 0))

type Props = {
  side: RentAmendmentSide
  offerId?: string
  flatId?: string
  /** Amounts valid today — the "before" column of the amendment. */
  current: Prices
  /** Initial "after" values; defaults to the current amounts. */
  prefill?: Prices
  /**
   * Initial effective month (yyyy-MM); defaults to next month, on the
   * landlord side to the day after the running sublease ends.
   */
  initialMonth?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * New rent and advance amounts from a chosen month, sent to every party of
 * the contract as an amendment to sign in DocuSeal. Nothing changes on the
 * offer/flat until everyone has signed; the backend applies the amounts to
 * the rents from the effective month on.
 *
 * On the tenant side the change can instead be a unilateral notice (sublease
 * contract Art. IV 9–10): no signatures, only increases, effective from the
 * month after it is sent. The contract caps it at inflation — that is left to
 * the admin, the dialog only shows the increase in percent.
 */
export function RentAmendmentDialog(props: Props) {
  const {
    side,
    offerId,
    flatId,
    current,
    prefill,
    initialMonth,
    open,
    onOpenChange
  } = props
  const { t } = useTranslation('amendments')
  const { confirm, dialog } = useConfirm()
  const createAmendment = useCreateRentAmendment()
  const preview = useRentAmendmentPreview()
  const isLandlord = side === 'LANDLORD'
  const landlordDefault = useLandlordAmendmentDefault(
    isLandlord && open ? flatId : undefined
  )

  const [kind, setKind] = useState<RentAmendmentKind>('AMENDMENT')
  // The first day of a month (yyyy-MM-dd); the tenant side picks a month, the
  // landlord side a date — usually 1st December, 1st January… or any day
  // before the flat has a sublease contract.
  const [effectiveFrom, setEffectiveFrom] = useState('')
  // Whether the landlord default (loaded after opening) may still overwrite
  // the date — not once the admin has picked one.
  const [awaitingDefault, setAwaitingDefault] = useState(false)
  const [values, setValues] = useState<Record<PriceField, string>>(
    {} as Record<PriceField, string>
  )

  // Reset the form every time the dialog opens, so a prefill passed by the
  // landlord-increase prompt is not overwritten by stale values.
  const [lastOpen, setLastOpen] = useState(false)
  if (open !== lastOpen) {
    setLastOpen(open)
    if (open) {
      const start = prefill ?? current
      setKind(side === 'TENANT' ? 'NOTICE' : 'AMENDMENT')
      const fallback = format(addMonths(new Date(), 1), 'yyyy-MM')
      setEffectiveFrom(
        initialMonth
          ? `${initialMonth}-01`
          : (landlordDefault.data?.effective_from ?? `${fallback}-01`)
      )
      setAwaitingDefault(isLandlord && !initialMonth)
      setValues(
        Object.fromEntries(
          PRICE_FIELDS.map((f) => [f, String(round2(start[f] || 0))])
        ) as Record<PriceField, string>
      )
    }
  }

  if (awaitingDefault && landlordDefault.data) {
    setAwaitingDefault(false)
    setEffectiveFrom(landlordDefault.data.effective_from)
  }
  const pickDate = (value: string) => {
    setAwaitingDefault(false)
    setEffectiveFrom(value)
  }

  const next = Object.fromEntries(
    PRICE_FIELDS.map((f) => [f, Number(values[f]) || 0])
  ) as Prices
  const unchanged = PRICE_FIELDS.every(
    (f) => round2(next[f]) === round2(current[f] || 0)
  )
  const isNotice = kind === 'NOTICE'
  // A notice takes effect from the month after it is sent at the earliest.
  const minDate = format(
    startOfMonth(addMonths(new Date(), isNotice ? 1 : 0)),
    'yyyy-MM-dd'
  )
  // Until the flat has a sublease contract, the landlord side may start on
  // any day — the backend splits that month's payment by days.
  const notFirstOfMonth =
    !!effectiveFrom &&
    !effectiveFrom.endsWith('-01') &&
    !(isLandlord && landlordDefault.data?.any_date_allowed)
  const decreased = PRICE_FIELDS.some(
    (f) => round2(next[f]) < round2(current[f] || 0)
  )
  const invalid =
    !effectiveFrom ||
    effectiveFrom < minDate ||
    notFirstOfMonth ||
    next.rent <= 0 ||
    PRICE_FIELDS.some((f) => next[f] < 0 || values[f] === '') ||
    (isNotice && decreased)

  const body = (): RentAmendmentBody => ({
    side,
    kind,
    offer: offerId,
    flat: flatId,
    effective_from: effectiveFrom,
    ...next
  })

  const send = async () => {
    const ok = await confirm({
      title: t(isNotice ? 'notice_confirm_title' : 'confirm_title'),
      description: t(
        isNotice ? 'notice_confirm_description' : 'confirm_description'
      ),
      confirmText: t(isNotice ? 'notice_confirm_send' : 'confirm_send'),
      cancelText: t('cancel')
    })
    if (!ok) return
    createAmendment.mutate(body(), { onSuccess: () => onOpenChange(false) })
  }

  const totalBefore = sum(current)
  const totalAfter = sum(next)
  const percent =
    totalBefore > 0
      ? round2(((totalAfter - totalBefore) / totalBefore) * 100)
      : 0

  return (
    <>
      {dialog}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <TrendingUp className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle>
                  {t(
                    isNotice
                      ? 'title_notice'
                      : side === 'TENANT'
                        ? 'title_tenant'
                        : 'title_landlord'
                  )}
                </DialogTitle>
                <p className="m-0 mt-1 text-sm text-muted-foreground">
                  {t(isNotice ? 'hint_notice' : 'hint')}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {side === 'TENANT' && (
              <div className="space-y-1.5">
                <Label>{t('kind')}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {(['NOTICE', 'AMENDMENT'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setKind(option)}
                      className={cn(
                        'rounded-xl border px-3 py-2 text-left transition-colors',
                        kind === option
                          ? 'border-primary bg-primary/5'
                          : 'border-ink/[0.08] hover:border-ink/20'
                      )}
                    >
                      <div className="text-sm font-semibold text-ink">
                        {t(`kinds.${option}`)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t(`kind_hints.${option}`)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLandlord ? (
              <div className="space-y-1.5">
                <Label>{t('effective_from_date')}</Label>
                <DatePicker
                  value={effectiveFrom}
                  onChange={(value) => pickDate(value ?? '')}
                  min={minDate}
                  invalid={notFirstOfMonth}
                />
                <p
                  className={cn(
                    'm-0 text-xs',
                    notFirstOfMonth
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  )}
                >
                  {notFirstOfMonth
                    ? t('effective_from_first_day')
                    : landlordDefault.data?.tenant_contract_end
                      ? t('effective_from_tenant_end', {
                          date: format(
                            parseISO(landlordDefault.data.tenant_contract_end),
                            'dd.MM.yyyy'
                          )
                        })
                      : landlordDefault.data
                        ? t('effective_from_no_sublease')
                        : null}
                </p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{t('effective_from')}</Label>
                {/* Not <input type="month"> — Safari and Firefox render it as
                    a plain text field. */}
                <DatePicker
                  picker="month"
                  value={effectiveFrom}
                  onChange={(value) => setEffectiveFrom(value ?? '')}
                  min={minDate}
                  placeholder={t('effective_from')}
                />
              </div>
            )}

            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_7rem_8rem] gap-3 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <span>{t('item')}</span>
                <span className="text-right">{t('current')}</span>
                <span>{t('new')}</span>
              </div>
              {PRICE_FIELDS.map((field) => {
                const changed =
                  round2(next[field]) !== round2(current[field] || 0)
                return (
                  <div
                    key={field}
                    className="grid grid-cols-[1fr_7rem_8rem] items-center gap-3"
                  >
                    <Label className="text-sm">{t(`fields.${field}`)}</Label>
                    <span className="text-right text-sm text-muted-foreground">
                      {money(current[field] || 0)}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={values[field] ?? ''}
                      onChange={(e) =>
                        setValues((v) => ({ ...v, [field]: e.target.value }))
                      }
                      className={cn(changed && 'border-primary font-semibold')}
                    />
                  </div>
                )
              })}
              <div className="grid grid-cols-[1fr_7rem_8rem] items-center gap-3 border-t border-ink/[0.06] pt-2 text-sm font-semibold text-ink">
                <span>{t('total')}</span>
                <span className="text-right">{money(totalBefore)}</span>
                <span className="px-4">
                  {money(totalAfter)}
                  {totalAfter !== totalBefore && (
                    <span
                      className={cn(
                        'ml-1 text-xs',
                        totalAfter > totalBefore ? 'text-primary' : 'text-peach'
                      )}
                    >
                      ({totalAfter > totalBefore ? '+' : ''}
                      {money(round2(totalAfter - totalBefore))})
                    </span>
                  )}
                </span>
              </div>
            </div>

            {isNotice && (
              <div
                className={cn(
                  'flex items-start gap-2 rounded-xl px-3 py-2 text-xs text-ink',
                  decreased ? 'bg-destructive/10' : 'bg-peach/15'
                )}
              >
                <AlertTriangle
                  className={cn(
                    'mt-0.5 h-3.5 w-3.5 shrink-0',
                    decreased ? 'text-destructive' : 'text-peach'
                  )}
                />
                {decreased
                  ? t('notice_decrease')
                  : t('notice_limit', {
                      percent: percent.toLocaleString('sk-SK')
                    })}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghostDark"
              onClick={() => preview.mutate(body())}
              disabled={invalid || unchanged || preview.isPending}
            >
              <Eye className="h-4 w-4" />
              {t(isNotice ? 'preview_notice' : 'preview')}
            </Button>
            <Button
              onClick={send}
              disabled={invalid || unchanged || createAmendment.isPending}
            >
              {isNotice ? (
                <Send className="h-4 w-4" />
              ) : (
                <FileSignature className="h-4 w-4" />
              )}
              {t(isNotice ? 'send_notice' : 'send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
